/**
 * MeetingsPage
 * Main dashboard for viewing and managing meetings, MoMs, and JIRA integration
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar } from 'lucide-react';
import { AppLayout } from '../../components/layout';
import { MeetingCard, StatsBar, FilterBar } from '../../components/meetings';
import { Button } from '../../components/ui/Button';
import { meetingApi } from '../../services/api';
import type { Meeting, MeetingStats, MeetingFilters } from '../../types';
import styles from './MeetingsPage.module.css';

export const MeetingsPage: React.FC = () => {
  const navigate = useNavigate();

  // Data state
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [stats, setStats] = useState<MeetingStats>({
    totalMeetings: 0,
    momsGenerated: 0,
    storiesCreated: 0,
    pendingReviews: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [filters, setFilters] = useState<MeetingFilters>({});
  const [activeTab, setActiveTab] = useState<'upcoming' | 'past'>('upcoming');

  // Load meetings and stats
  const loadMeetings = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [meetingsResponse, statsResponse] = await Promise.all([
        meetingApi.getMeetings(filters),
        meetingApi.getMeetingStats(),
      ]);

      if (meetingsResponse.success && meetingsResponse.data) {
        setMeetings(meetingsResponse.data);
      }

      if (statsResponse.success && statsResponse.data) {
        setStats(statsResponse.data);
      }
    } catch (err) {
      console.error('Failed to load meetings:', err);
      setError('Failed to load meetings. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadMeetings();
  }, [loadMeetings]);

  // Filter meetings by upcoming/past
  const now = new Date();
  const upcomingMeetings = meetings.filter(
    (m) => new Date(m.scheduledStart) >= now
  );
  const pastMeetings = meetings.filter(
    (m) => new Date(m.scheduledStart) < now
  );

  const displayedMeetings = activeTab === 'upcoming' ? upcomingMeetings : pastMeetings;

  const handleMeetingClick = useCallback((meetingId: string) => {
    navigate(`/meetings/${meetingId}`);
  }, [navigate]);

  const handleClearFilters = useCallback(() => {
    setFilters({});
  }, []);

  return (
    <AppLayout>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.titleSection}>
            <Calendar size={32} className={styles.titleIcon} />
            <div>
              <h1 className={styles.title}>Meetings</h1>
              <p className={styles.subtitle}>
                Review meeting minutes, manage action items, and push stories to JIRA
              </p>
            </div>
          </div>
        </div>

        <StatsBar stats={stats} loading={isLoading} />

        <FilterBar
          filters={filters}
          onFiltersChange={setFilters}
          onClearFilters={handleClearFilters}
        />

        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'upcoming' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('upcoming')}
          >
            Upcoming Meetings ({upcomingMeetings.length})
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'past' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('past')}
          >
            Past Meetings ({pastMeetings.length})
          </button>
        </div>

        {isLoading && (
          <div className={styles.loading}>
            <div className={styles.spinner} />
            <p>Loading meetings...</p>
          </div>
        )}

        {error && (
          <div className={styles.error}>
            <p>{error}</p>
            <Button onClick={loadMeetings} variant="primary" size="sm">
              Retry
            </Button>
          </div>
        )}

        {!isLoading && !error && displayedMeetings.length === 0 && (
          <div className={styles.empty}>
            <Calendar size={64} className={styles.emptyIcon} />
            <h3 className={styles.emptyTitle}>
              {activeTab === 'upcoming' ? 'No Upcoming Meetings' : 'No Past Meetings'}
            </h3>
            <p className={styles.emptyText}>
              {activeTab === 'upcoming'
                ? 'Invite the BrainIQ bot to your meetings to get started.'
                : 'Past meetings with recorded MoMs will appear here.'}
            </p>
          </div>
        )}

        {!isLoading && !error && displayedMeetings.length > 0 && (
          <div className={styles.meetingsList}>
            {displayedMeetings.map((meeting) => (
              <MeetingCard
                key={meeting.id}
                meeting={meeting}
                onClick={() => handleMeetingClick(meeting.id)}
              />
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default MeetingsPage;
